import { memo } from "react";
import { Handle, Position, type NodeProps, type Node } from "@xyflow/react";
import { Crosshair } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { NodeExpandToggle } from "./node-expand-toggle";

export type StrategicNodeType = "college_strategic_pillar" | "college_goal" | "focus_area" | "tactic";

export interface StrategicNodeData extends Record<string, unknown> {
  nodeId: string;
  nodeType: StrategicNodeType;
  title: string;
  horizon: string | null;
  referenceCode: string | null;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onFocusNode: (id: string) => void;
  onShowDetail: ((id: string) => void) | null;
}

export type StrategicNodeFlowType = Node<StrategicNodeData, "strategic">;

const TYPE_LABELS: Record<StrategicNodeType, string> = {
  college_strategic_pillar: "Pillar",
  college_goal: "College Objective",
  focus_area: "ITS Focus Area",
  tactic: "ITS Strategic Initiative",
};

const HORIZON_LABELS: Record<string, string> = {
  "7_year": "7-Year",
  "3_year": "3-Year",
  "18_month": "18-Month",
};

const COLLEGE_TYPES = new Set<StrategicNodeType>(["college_strategic_pillar", "college_goal"]);

function isCollegeLayer(nodeType: StrategicNodeType): boolean {
  return COLLEGE_TYPES.has(nodeType);
}

function StrategicNodeComponent({ data }: NodeProps<StrategicNodeFlowType>) {
  const isCollege = isCollegeLayer(data.nodeType);
  const accentClass = isCollege
    ? "border-l-[hsl(40,63%,48%)]"
    : "border-l-[hsl(213,52%,63%)]";

  return (
    <div
      className={`w-70 rounded-lg border bg-[hsl(var(--card))] shadow-sm border-l-4 ${accentClass}`}
    >
      <Handle type="target" position={Position.Top} className="!bg-[hsl(var(--primary))]" />

      {/* Header with type label */}
      <div className="flex items-center justify-between border-b px-3 py-1.5">
        <Badge
          variant={isCollege ? "accent" : "info"}
          className="text-[10px] px-1.5 py-0"
        >
          {TYPE_LABELS[data.nodeType]}
        </Badge>
        <button
          type="button"
          className="rounded p-0.5 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] transition-colors"
          onClick={(e) => { e.stopPropagation(); data.onFocusNode(data.nodeId); }}
          title="Focus on subtree"
        >
          <Crosshair className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Body - clickable to show detail */}
      <div
        className={cn("px-3 py-2", data.onShowDetail && "cursor-pointer hover:bg-[hsl(var(--muted))] transition-colors")}
        onClick={() => data.onShowDetail?.(data.nodeId)}
        role={data.onShowDetail ? "button" : undefined}
      >
        <p className="text-sm font-medium leading-tight line-clamp-2">{data.title}</p>
        {data.horizon && (
          <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
            {HORIZON_LABELS[data.horizon] ?? data.horizon}
          </p>
        )}
        {data.referenceCode && (
          <p className="mt-1 text-xs font-mono text-[hsl(var(--muted-foreground))]">
            {data.referenceCode}
          </p>
        )}
      </div>

      {/* Footer with expand toggle */}
      {data.hasChildren && (
        <div className="border-t px-3 py-1.5">
          <NodeExpandToggle
            nodeId={data.nodeId}
            isExpanded={data.isExpanded}
            onToggleExpand={data.onToggleExpand}
          />
        </div>
      )}

      <Handle type="source" position={Position.Bottom} className="!bg-[hsl(var(--primary))]" />
    </div>
  );
}

export const StrategicNode = memo(StrategicNodeComponent);
